<script lang="ts">
	// Formating
	import { format } from 'svelte-i18n';
	//Helpers
	import {
		buildChartControlsData,
		getInitLoadedPeriodStatistics,
		LAYER_RANGE_OPTIONS,
	} from 'src/helpers/statistics/ui/chart_control_helpers';
	import { getStatistics } from 'src/helpers/statistics/request/chart_get_statistics_helpers';
	import { getDeviceChartsConfig } from 'src/helpers/statistics/config/device_chart_config';
	// Design system components
	import {
		ControlsContainer,
		Icon,
		Text,
		LayerChartWrapper,
		type StatisticsData,
	} from '@Shelly/shelly-design-system/index';
	//Components
	import ChartTitle from './ChartTitle.svelte';
	import LayerChartRenderer from './LayerChartRenderer.svelte';
	//Types
	import type {
		ChartConfig,
		ChartConfigInterface,
		ChartDataInterface,
		ChartStatisticRangePeriod,
	} from 'src/types/Chart';
	import type { Device } from 'src/types/Device';
	import { mapIconToThin } from 'src/helpers/new-layout-helpers';
	import { getChartIcon } from 'src/helpers/statistics/config/chart_icons_helper';
	import { isDistance, real_id } from 'src/helpers/interface_helpers';
	import { devices_settings } from 'src/stores/device-runtime/status';

	export let device: Device;
	export let deviceChartsConfig: ChartConfigInterface[] = null;
	export let legacyStyle = false;

	let className = 'mb-s';
	export { className as class };

	const resolvedDeviceChartsConfig = deviceChartsConfig || getDeviceChartsConfig(device);
	const chartConfigs: ChartConfig[] = resolvedDeviceChartsConfig.map((i) => i.charts).flat();
	const selectedChartIndex = 0;
	const chartConfig: ChartConfig = chartConfigs[selectedChartIndex];
	const icon = mapIconToThin(getChartIcon(chartConfig.name));
	const distanceUnit =
		device?.ht_ui?.unit || $devices_settings[real_id(device.id)]?.ht_ui?.unit || 'mm';

	const onSelectPeriod = (currentPeriod: ChartStatisticRangePeriod) => {
		const controlsData = buildChartControlsData(device, currentPeriod);
		statisticsData = getStatistics(
			device,
			controlsData,
			chartConfig,
			chartConfig.url,
			0,
			false,
		) as Promise<StatisticsData>;
	};
	const activePeriod =
		getInitLoadedPeriodStatistics(LAYER_RANGE_OPTIONS).period || LAYER_RANGE_OPTIONS[1].period;

	let statisticsData: Promise<StatisticsData>;
	onSelectPeriod(activePeriod);

	const handleKeydown = (event: KeyboardEvent) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			(event.currentTarget as HTMLElement).click();
		}
	};
	const getThumbChartData = (chartData: ChartDataInterface): ChartDataInterface => ({
		...chartData,
		series: chartData.series.filter((series) => !series.prevPeriod),
		...(isDistance(device.type)
			? {
					units: distanceUnit === 'in' ? 'in' : 'mm',
					type: 'area' as const,
				}
			: {}),
	});
</script>

{#if chartConfig}
	<div
		class={className}
		role="button"
		tabindex="0"
		aria-label={$format('label_view_statistics', {
			default: `View ${chartConfig.chartTitle || chartConfig.title || chartConfig.name} statistics`,
		})}
		on:click
		on:keydown={handleKeydown}
	>
		<ControlsContainer color={legacyStyle ? 'color-base' : 'color-0'} size="s">
			<svelte:fragment slot="first">
				<Icon {icon} size="s" class="mb-s" />
				<ChartTitle
					styleType="h2"
					class="text-bold mb-s text-capitalize"
					title={chartConfig.chartTitle}
				/>
				<Text noWrap styleType="tiny"
					>{$format(`option_${activePeriod}`, {
						default: activePeriod,
					})}</Text
				>
				<div class="pointer-events-none">
					<LayerChartWrapper
						{statisticsData}
						class="thumb-chart mb-s"
						noDataLabel={$format('no_data', { default: 'No data available' })}
						let:statisticsData
					>
						<LayerChartRenderer
							chartData={getThumbChartData(statisticsData.chartData)}
							legend={undefined}
							thumbMode
						/>
					</LayerChartWrapper>
				</div>
			</svelte:fragment>
		</ControlsContainer>
	</div>
{/if}
